import classNames from 'classnames';
import { useCallback } from 'react';

import { Radio, RadioGroup, Text } from '@revelio/ui';

import {
  METRO_AREA_TAXONOMY_LABEL,
  STATE_COUNTY_TAXONOMY_LABEL,
} from '../../selection-lists/utils/geographyTaxonomyLabels';
import { PreferenceEditorSectionHeading } from '../components/PreferenceEditorSectionHeading';
import {
  type EconomicImpactGeography,
  isEconomicImpactGeography,
  PreferenceScope,
  resolveEconomicImpactGeography,
  UserPreferenceKey,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import layoutStyles from './economic-impact-editor.module.css';
import styles from './preference-editors.module.css';
import type {
  PreferenceEditorProps,
  PreferenceMeta,
} from './preferenceEditors';

export const meta: PreferenceMeta = {
  id: UserPreferenceKey.EconomicImpactGeography,
  label: 'Geography',
};

/**
 * Which geographies the Economic Impact page places investments in and
 * reports on. Like the industry classification beside it, only the
 * economic-impact page's own preferences button surfaces this editor, and the
 * choice is a single GLOBAL record.
 */
export default function EconomicImpactGeographyEditor({
  onPreferenceUpdate,
  preferences,
  diff,
}: PreferenceEditorProps) {
  // Derived from props rather than mirrored in state: the modal's provider
  // already re-renders the editor on every store change.
  const geography = resolveEconomicImpactGeography(
    preferences[UserPreferenceKey.EconomicImpactGeography]
  );

  const onUpdate = useCallback(
    (value: EconomicImpactGeography) => {
      // Single GLOBAL choice: always write the GLOBAL record regardless of
      // which page's modal made the change (same pattern as DataAdjustments).
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.EconomicImpactGeography,
        value
      );
      onPreferenceUpdate?.(UserPreferenceKey.EconomicImpactGeography, value);
    },
    [onPreferenceUpdate]
  );

  return (
    <>
      <PreferenceEditorSectionHeading
        title="Geography"
        indicateDiff={diff?.includes(meta.id) ?? false}
        tooltip="Where you place an investment, and the places its impact is shown by."
      />
      <RadioGroup
        value={geography}
        onChange={(value) => {
          if (isEconomicImpactGeography(value)) onUpdate(value);
        }}
        className={styles.radioGroup}
      >
        <div className={layoutStyles.choiceList}>
          <div className={layoutStyles.choice}>
            <Radio
              value="city"
              className={styles.appearanceRadio}
              data-testid="economic-impact-geography-city"
            >
              {METRO_AREA_TAXONOMY_LABEL}
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Metro Area › City
            </Text>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              Income brackets are only broken down by county.
            </Text>
          </div>
          <div className={layoutStyles.choice}>
            <Radio
              value="county"
              className={styles.appearanceRadio}
              data-testid="economic-impact-geography-county"
            >
              {STATE_COUNTY_TAXONOMY_LABEL}
            </Radio>
            <Text
              fontSize="x-sm"
              className={classNames(
                styles.description,
                styles.descriptionIndent
              )}
            >
              State › County
            </Text>
          </div>
        </div>
      </RadioGroup>
    </>
  );
}
